import { Component, ErrorHandler, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, map, Subject, take, takeUntil } from 'rxjs';

import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { TranslatePipe } from '@next/core-lib/i18n';
import { UnsavedChangesService } from '../../../shared/services/unsaved-changes.service';
import { CropYearSelectorComponent } from '../../../shared/components/crop-year-selector/crop-year-selector.component';
import { TgaCultivationAdviceComponent } from '../../advice/cultivation/tga-cultivation-advice/tga-cultivation-advice.component';
import { TgaEvaluationAdviceComponent } from '../../advice/evaluation/tga-evaluation-advice/tga-evaluation-advice.component';
import { TgaGeneralAdviceComponent } from '../../advice/general/tga-general-advice.component';
import { TgaPlantingAdviceComponent } from '../../advice/planting/tga-planting-advice.component';
import { TGASoilAdviceComponent } from '../../advice/soil/tga-soil-advice.component';
import { StepperService } from '../../services/stepper.service';
import { NoQuestionsComponent } from '../../shared/components/no-questions/no-questions.component';
import { StepStatus, TgaState, TgaSteps, VALID_STEP_NAMES } from '../../shared/models/models';
import { loadCropFields, QuestionnaireActions } from '../../store/tga.actions';
import {
  getTgaCropYear,
  selectActiveStep,
  selectLastGenericStepCompleted,
  selectLastSpecificStepCompleted,
} from '../../store/tga.selectors';
import { BackArrowButtonComponent } from '../back-arrow-button/back-arrow-button.component';
import { GenericGeneralQuestionnaireComponent } from '../questionnaire/questions/generic/generic-general-questionnaire/generic-general-questionnaire.component';
// eslint-disable-next-line max-len
import { GenericPlantingQuestionnaireComponent } from '../questionnaire/questions/generic/generic-planting-questionnaire/generic-planting-questionnaire.component';
import { GenericSoilQuestionnaireComponent } from '../questionnaire/questions/generic/generic-soil-questionnaire/generic-soil-questionnaire.component';
import { SpecificPlantingQuestionnaireComponent } from '../questionnaire/questions/specific/planting-questionnaire/specific-planting-questionnaire.component';
import { SpecificSoilQuestionnaireComponent } from '../questionnaire/questions/specific/soil-questionnaire/specific-soil-questionnaire.component';
// eslint-disable-next-line max-len
import { SpecificGeneralQuestionnaireComponent } from '../questionnaire/questions/specific/specific-general-questionnaire/specific-general-questionnaire.component';
import { StepperComponent } from '../stepper/stepper.component';

@Component({
  selector: 'app-questionnaire-layout',
  templateUrl: './questionnaire-layout.component.html',
  styleUrl: './questionnaire-layout.component.scss',
  imports: [
    BackArrowButtonComponent,
    StepperComponent,
    MatButton,
    GenericGeneralQuestionnaireComponent,
    SpecificGeneralQuestionnaireComponent,
    GenericSoilQuestionnaireComponent,
    SpecificSoilQuestionnaireComponent,
    SpecificPlantingQuestionnaireComponent,
    GenericPlantingQuestionnaireComponent,
    NoQuestionsComponent,
    TgaGeneralAdviceComponent,
    TGASoilAdviceComponent,
    TgaPlantingAdviceComponent,
    TgaCultivationAdviceComponent,
    TgaEvaluationAdviceComponent,
    AsyncPipe,
    LowerCasePipe,
    TranslatePipe,
    CropYearSelectorComponent,
  ],
})
export class QuestionnaireLayoutComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<TgaState>>(Store);
  private readonly router = inject(Router);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly route = inject(ActivatedRoute);
  private readonly stepperService = inject(StepperService);
  private readonly unsavedChangesService = inject(UnsavedChangesService);

  @ViewChild(StepperComponent) stepper!: StepperComponent;
  private readonly destroy$ = new Subject<void>();
  private beforeUnloadHandler?: (event: BeforeUnloadEvent) => void;
  mode$ = this.route.params.pipe(map((params) => params['mode']));
  questionnaireType$ = this.route.url.pipe(map((url) => url[4].path || null));
  lastCompleted$ = combineLatest([
    this.questionnaireType$,
    this.store.select(selectLastGenericStepCompleted),
    this.store.select(selectLastSpecificStepCompleted),
  ]).pipe(
    map(([questionnaireType, lastGeneric, lastSpecific]: [string | null, TgaSteps | null, TgaSteps | null]) => {
      return questionnaireType === 'generic' ? lastGeneric : lastSpecific;
    }),
  );
  step: TgaSteps = TgaSteps.General;
  cropYear = 0;
  cropFieldId: number | null = null;

  activeStep$ = this.store.select(selectActiveStep);
  nextStepTranslationKey$ = this.activeStep$.pipe(
    map((activeStep) => this.stepperService.getNextStepName(activeStep.translationKey)),
  );
  isNextStepEnabled$ = combineLatest([
    this.nextStepTranslationKey$,
    this.lastCompleted$,
    this.mode$,
  ]).pipe(
    map(([nextStepName, lastCompleted, mode]) => {
      if (!nextStepName || !lastCompleted) {
        return false;
      }
      return mode === 'questionnaire' ? this.stepperService.isStepEnabled(nextStepName, lastCompleted) : true;
    }),
  );

  stepStatus = StepStatus;

  constructor() {
    const snapshot = this.route.snapshot.params;
    const cropFieldId = Number(snapshot['cropFieldId']);
    this.cropFieldId = Number.isFinite(cropFieldId) ? cropFieldId : null;
    const cropYear = Number(snapshot['cropYear']);
    this.cropYear = Number.isFinite(cropYear) ? cropYear : 0;
    const stepParam = snapshot['step'] ?? TgaSteps.General;
    const normalizedStep = this.normalizeStepName(stepParam);
    this.step = normalizedStep;
    this.stepperService.moveToStep(normalizedStep);
  }

  private normalizeStepName(stepParam: string): TgaSteps {
    // Check if it's a valid step name
    const step = VALID_STEP_NAMES.find((step) => step.toLocaleLowerCase() === stepParam.toLocaleLowerCase());
    if (step) {
      return step;
    }

    // Default to General if invalid
    return TgaSteps.General;
  }

  ngOnInit(): void {
    this.store
      .select(getTgaCropYear)
      .pipe(take(1))
      .subscribe((selectedCropYear) => {
        this.cropYear = selectedCropYear;
        this.normalizeRouteCropYear(selectedCropYear);

        if (this.cropFieldId !== null) {
          this.store.dispatch(
            QuestionnaireActions.setSpecificQuestionnaireContext({
              cropFieldId: this.cropFieldId,
              cropYear: this.cropYear,
            }),
          );
        }

        combineLatest([this.questionnaireType$, this.lastCompleted$])
          .pipe(take(1))
          .subscribe(([questionnaireType, lastCompleted]) => {
            if ((questionnaireType == 'specific' || questionnaireType == 'generic') && !lastCompleted) {
              this.store.dispatch(loadCropFields({ cropYear: this.cropYear }));
            }
          });

        this.activeStep$.pipe(takeUntil(this.destroy$)).subscribe((step) => {
          if (step) {
            this.step = step.translationKey;
            const urlStep = this.route.snapshot.params['step'];
            if (urlStep === step.translationKey) return;
            this.cropFieldId = this.route.snapshot.params['cropFieldId'];
            const mode = this.route.snapshot.params['mode'];
            if (mode == 'advice') {
              this.router.navigate(['/tga/cropfields', step.translationKey, mode, this.cropYear, this.cropFieldId], {
                queryParams: this.route.snapshot.queryParams,
              });
            } else {
              const questionnaireType = this.route.snapshot.url[4]?.path;
              if (!questionnaireType) return;
              if (questionnaireType === 'generic') {
                this.router.navigate(['/tga/cropfields', step.translationKey, mode, questionnaireType, this.cropYear], {
                  queryParams: this.route.snapshot.queryParams,
                });
                return;
              }
              this.router.navigate(['/tga/cropfields', step.translationKey, mode, questionnaireType, this.cropYear, this.cropFieldId], {
                queryParams: this.route.snapshot.queryParams,
              });
            }
          }
        });
      });

    this.beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      if (this.unsavedChangesService.hasUnsavedChanges()) {
        event.preventDefault();
        // For older browsers that might still use returnValue
        event.returnValue = '';
        return '';
      }
      return undefined;
    };

    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private normalizeRouteCropYear(selectedCropYear: number): void {
    const routeCropYear = Number(this.route.snapshot.params['cropYear']);
    if (routeCropYear === selectedCropYear) return;

    const mode = this.route.snapshot.params['mode'];
    const questionnaireType = this.route.snapshot.url[4]?.path;
    let route: (string | number)[];

    if (mode === 'advice') {
      route = ['/tga/cropfields', this.step, mode, selectedCropYear, this.cropFieldId];
    } else if (questionnaireType === 'generic') {
      route = ['/tga/cropfields', this.step, mode, questionnaireType, selectedCropYear];
    } else if (questionnaireType === 'specific') {
      route = ['/tga/cropfields', this.step, mode, questionnaireType, selectedCropYear, this.cropFieldId];
    } else {
      return;
    }

    this.router.navigate(route, { queryParams: this.route.snapshot.queryParams }).catch((error: unknown) =>
      this.errorHandler.handleError(error),
    );
  }

  ngOnDestroy(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  onNextQuestionnaireClick(): void {
    this.stepperService.moveToNextStep();
  }

  navigateToCropFieldSelection(): void {
    this.router.navigate(['/tga/cropfields'], { queryParams: this.route.snapshot.queryParams });
  }

  navigateToTGAInsights = (): void => {
    this.router.navigate(['/tga'], { queryParams: this.route.snapshot.queryParams });
  };

  navigateToAdvices(): void {
    if (this.cropFieldId == null) return;
    this.router.navigate([`/tga/cropfields/${this.step}/advice`, this.cropYear, this.cropFieldId], {
      queryParams: this.route.snapshot.queryParams,
    });
  }

  navigateToQuestionnaire(): void {
    if (this.cropFieldId == null) return;
    this.router.navigate([`/tga/cropfields/${this.step}/questionnaire/specific`, this.cropYear, this.cropFieldId], {
      queryParams: this.route.snapshot.queryParams,
    });
  }
}
